import dayjs, { Dayjs } from 'dayjs';
import { t } from 'i18next';

import { DataTableColumn } from '~components/DataTable/dataTableColumn';
import { Timesheet } from '~hooks/useTimesheets/models';
import TimesheetStatusChip from '~pages/Approvals/shared/TimesheetStatusChip';

const emDash = '-';
const DISPLAY_DATE_FORMAT = 'MMM D, YYYY';

const formatDisplayDate = (date: Dayjs | null) =>
  date ? dayjs.tz(date).format(DISPLAY_DATE_FORMAT) : emDash;

export type TimesheetRow = {
  id: string;
  linkedTo: string;
  driver: string;
  shiftDate: Dayjs | null;
  timesheetNumber: string;
  aiProcessed: boolean;
  uploadedAt: Dayjs;
};

export const transformTimesheetToRow = (timesheet: Timesheet): TimesheetRow => {
  const { job, driverDay } = timesheet;

  const linkedTo = job
    ? t('approvals.timesheets.linked_to_job', { jobId: job.jobId })
    : driverDay
      ? t('approvals.timesheets.linked_to_shift', { driverDayId: driverDay.driverDayId })
      : emDash;

  const driverName = driverDay?.driver
    ? [driverDay.driver.firstName, driverDay.driver.lastName]
        .filter((namePart) => Boolean(namePart))
        .join(' ')
    : '';

  // A job-linked timesheet has no driver-day date, so fall back to the job start, then to
  // the AI-extracted date as a last resort.
  const shiftDate =
    driverDay?.driverDayDate ?? job?.jobStartAt ?? timesheet.extractedDate ?? null;

  return {
    id: timesheet.id,
    linkedTo,
    driver: driverName || emDash,
    shiftDate,
    timesheetNumber: timesheet.timesheetNumber ?? timesheet.extractedNumber ?? emDash,
    aiProcessed: timesheet.aiProcessed,
    uploadedAt: timesheet.createdAt,
  };
};

export const TIMESHEET_COLUMNS: Array<DataTableColumn<TimesheetRow>> = [
  {
    field: 'linkedTo',
    headerName: t('approvals.timesheets.linked_to'),
    valueGetter: (row) => row.linkedTo,
  },
  {
    field: 'driver',
    headerName: t('form_fields.driver'),
    valueGetter: (row) => row.driver,
  },
  {
    field: 'shiftDate',
    headerName: t('approvals.timesheets.shift_date'),
    valueGetter: (row) => formatDisplayDate(row.shiftDate),
  },
  {
    field: 'timesheetNumber',
    headerName: t('approvals.timesheets.timesheet_number'),
    valueGetter: (row) => row.timesheetNumber,
  },
  {
    field: 'aiProcessed',
    headerName: t('approvals.timesheets.status'),
    renderCell: (row) => <TimesheetStatusChip aiProcessed={row.aiProcessed} />,
  },
  {
    field: 'uploadedAt',
    headerName: t('approvals.timesheets.uploaded'),
    valueGetter: (row) => formatDisplayDate(row.uploadedAt),
  },
];
